import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { AuthUser } from '../api/auth.js';
import { setAuthToken, setOnAuthError } from '../api/client.js';
import { clearCryptoKey } from './crypto-key.js';
import { type AuthSession, clearSession, loadSession, saveSession } from './storage.js';

interface AuthContextValue {
  session: AuthSession | null;
  setSession: (s: AuthSession) => void;
  clear: () => void;
  promote: (token: string, expiresAt: string, user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSessionState] = useState<AuthSession | null>(() => {
    // Sincroniza el token con el cliente HTTP ANTES del primer render
    // para que cualquier useEffect de un hijo encuentre el token listo.
    const initial = loadSession();
    setAuthToken(initial?.token ?? null);
    return initial;
  });

  // Conecta el handler de errores de auth una vez al montar.
  useEffect(() => {
    setOnAuthError(() => {
      clearSession();
      setAuthToken(null);
      setSessionState(null);
    });
    return () => setOnAuthError(() => undefined);
  }, []);

  const setSession = useCallback((s: AuthSession) => {
    saveSession(s);
    setAuthToken(s.token);
    setSessionState(s);
  }, []);

  const clear = useCallback(() => {
    clearSession();
    setAuthToken(null);
    clearCryptoKey();
    setSessionState(null);
  }, []);

  const promote = useCallback((token: string, expiresAt: string, user: AuthUser) => {
    const next: AuthSession = { token, kind: 'full', expiresAt, user };
    saveSession(next);
    setAuthToken(token);
    setSessionState(next);
  }, []);

  const value = useMemo(
    () => ({ session, setSession, clear, promote }),
    [session, setSession, clear, promote],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}
